import React, { useEffect, useRef } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { handleImageErrorWithFallback } from '../../utils/imageManager';
import { renderCtaButton } from '../../utils/ctaHelper';
import { usePageTitle } from '../../hooks/usePageTitle';

const CorporateVariant = ({ heroContent, brand }) => {
  const { cta, domainConfig } = useTheme();
  const mainImageRef = useRef(null);
  const secondaryImageRef = useRef(null);
  
  // Get hostname-specific H1 title
  const h1Title = usePageTitle('home');

  // Reset image elements when domain changes
  useEffect(() => {
    if (mainImageRef.current) {
      Object.keys(mainImageRef.current.dataset).forEach(key => {
        if (key.startsWith('triedExtensions_')) {
          delete mainImageRef.current.dataset[key];
        }
      });
    }
    if (secondaryImageRef.current) {
      Object.keys(secondaryImageRef.current.dataset).forEach(key => {
        if (key.startsWith('triedExtensions_')) {
          delete secondaryImageRef.current.dataset[key];
        }
      });
    }
  }, [brand.id]);

  const { mainImage, secondaryImage, title, subtitle, features } = heroContent;

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section - Corporate Professional Design */}
      <section className="pt-20 pb-24 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-6">

          {/* Main Content Grid */}
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Left Column - Content */}
            <div className="space-y-8">
              <div>
                <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 leading-tight mb-6">
                  {h1Title || title}
                </h1>
                <p className="text-lg text-gray-600 leading-relaxed mb-8">
                  {subtitle}
                </p>
              </div>

              {/* Features - Corporate List */}
              <div className="space-y-4">
                {features.map((feature, index) => (
                  <div key={index} className="flex items-center space-x-4 p-4 bg-white rounded-lg border border-gray-200 shadow-sm">
                    <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                      <div className="w-3 h-3 bg-blue-600 rounded-full"></div>
                    </div>
                    <span className="text-gray-800 font-medium">{feature}</span>
                  </div>
                ))}
              </div>

              {/* CTA Buttons - Corporate Style */}
              <div className="flex space-x-4 pt-8">
                {renderCtaButton(cta?.primary, 'Request Demo', 'px-8 py-3 bg-blue-600 text-white font-semibold rounded-md hover:bg-blue-700 transition-colors duration-200 shadow-sm', domainConfig?.shopLink)}
                {renderCtaButton(cta?.secondary, 'Buy Now', 'px-8 py-3 border border-gray-300 text-gray-700 font-semibold rounded-md hover:border-gray-400 hover:bg-gray-50 transition-colors duration-200', domainConfig?.shopLink)}
              </div>
            </div>

            {/* Right Column - Images */}
            <div className="relative">
              {/* Main Image */}
              <div className="relative">
                <img 
                  ref={mainImageRef}
                  src={mainImage} 
                  alt={title}
                  className="w-full h-64 md:h-96 object-cover rounded-lg shadow-lg"
                  onError={(e) => handleImageErrorWithFallback(e, brand.id, 'hero-main')}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent rounded-lg"></div>
              </div>
              
              {/* Secondary Image - Professional Overlay */}
              <div className="absolute -bottom-6 -left-6 w-48 h-32 bg-white rounded-lg shadow-lg border border-gray-200 p-2">
                <img 
                  ref={secondaryImageRef}
                  src={secondaryImage} 
                  alt={`${title} - Secondary`}
                  className="w-full h-full object-cover rounded-md"
                  onError={(e) => handleImageErrorWithFallback(e, brand.id, 'hero-secondary')}
                />
              </div>

            </div>
          </div>
        </div>
      </section>

      {/* Stats Section - Corporate Metrics */}
      <section className="py-16 bg-gray-50">
        <div className="text-center mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">Put connected fleet intelligence to work to drive better outcomes</h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            An open platform for safe, sustainable and cost-effective mobility powered by AI and data intelligence.
          </p>
        </div>
        <div className="max-w-7xl mx-auto p-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {[
              { number: '50,000+', label: 'Global Clients', icon: '🏢' },
              { number: '80 billion', label: 'Data points daily', icon: '⚡' },
              { number: '4.7 million', label: 'Vehicle Subscriptions', icon: '📈' },
              { number: '25+', label: 'Years of Innovation', icon: '⭐' }
            ].map((stat, index) => (
              <div key={index} className="text-center p-4 bg-white rounded-lg shadow-sm border border-gray-200">
                <div className="text-3xl mb-2">{stat.icon}</div>
                <div className="text-3xl font-bold text-gray-900 mb-2">{stat.number}</div>
                <div className="text-sm text-gray-600 font-medium">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-6">
          {/* Awards and Certifications */}
          <div className="flex flex-col items-center space-y-8">
            {/* Main Awards Image */}
            <div className="max-w-6xl w-full">
              <img
                src={`/assets/domains/${brand.id}/awards.png`}
                alt="Industry Awards and Certifications"
                className="w-full h-auto object-contain"
              />
            </div>
            
            {/* Trust Badges */}
            <div className="flex flex-wrap justify-center items-center gap-6 opacity-70">
              <div className="flex items-center space-x-2 px-4 py-2 bg-white rounded-full shadow-sm border border-gray-200">
                <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                <span className="text-sm font-medium text-gray-700">99.9% Uptime</span>
              </div>
              <div className="flex items-center space-x-2 px-4 py-2 bg-white rounded-full shadow-sm border border-gray-200">
                <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                <span className="text-sm font-medium text-gray-700">24/7 Support</span>
              </div>
              <div className="flex items-center space-x-2 px-4 py-2 bg-white rounded-full shadow-sm border border-gray-200">
                <div className="w-3 h-3 bg-purple-500 rounded-full"></div>
                <span className="text-sm font-medium text-gray-700">Enterprise Ready</span>
              </div>
              <div className="flex items-center space-x-2 px-4 py-2 bg-white rounded-full shadow-sm border border-gray-200">
                <div className="w-3 h-3 bg-orange-500 rounded-full"></div>
                <span className="text-sm font-medium text-gray-700">Award Winning</span>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CorporateVariant;
